// Phase A.3 / 8B.2 — SIKA chat hook with SSE streaming.
//
// Manages message history, sends user messages to /api/sika/stream,
// processes SSE events for real-time token streaming, and falls back
// to /api/sika/chat if streaming is unavailable.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type { Database } from "../db/types.ts";
import type { AgentStep, AgentStepStatus } from "../pages/sika/SikaAgentDeploy.tsx";

type SikaMessageRow = Database["public"]["Tables"]["sika_messages"]["Row"];

/** Tool name → human-readable label for live deployment display. */
const TOOL_LABELS: Record<string, string> = {
  get_bankroll_state: "Checking bankroll state",
  calculate_scenarios: "Running compounding scenarios",
  create_goal: "Saving your goal",
  list_goals: "Loading your goals",
  update_goal: "Updating goal",
  link_chain: "Linking chain to goal",
  unlink_chain: "Unlinking chain",
  check_readiness: "Running readiness check",
  get_chain_status: "Checking chain progress",
  generate_artefact: "Generating document",
  build_slip: "Building your slip",
  create_chain: "Creating chain",
  calculate_multi_chain: "Calculating multi-chain",
  check_milestones: "Checking milestones",
  lookup_price: "Looking up price",
  generate_strategy_review: "Generating review",
  allocate_capital: "Allocating capital",
  record_outcome: "Recording outcome",
  second_opinion: "Running second opinion",
  activate_plan: "Activating plan",
  store_memory: "Storing memory",
  recall_memory: "Recalling memory",
  add_accountability_partner: "Adding partner",
  declare_bankroll: "Declaring bankroll",
};

export interface ChatMessage {
  readonly id: string;
  readonly role: "user" | "assistant" | "tool";
  readonly content: string;
  readonly tool_use: unknown | null;
  readonly card_type: string | null;
  readonly created_at: string;
}

export interface UseSikaChatState {
  messages: ChatMessage[];
  /** True while waiting for a response (streaming or polling). */
  sending: boolean;
  /** Partial text from streaming — updated per token. */
  streamingText: string;
  /** Live agent deployment steps — visible during streaming. */
  agentSteps: AgentStep[];
  error: string | null;
  goalId: string | null;
  sessionId: string | null;
  setGoalId: (id: string | null) => void;
  send: (text: string) => Promise<void>;
  newSession: () => void;
  /** Load a specific session by ID (for conversation history). */
  loadSession: (sessionId: string, goalId: string | null) => void;
  loadingHistory: boolean;
}

function rowToMessage(row: SikaMessageRow): ChatMessage {
  return {
    id: row.id,
    role: row.role as ChatMessage["role"],
    content: row.content ?? "",
    tool_use: row.tool_use,
    card_type: row.card_type,
    created_at: row.created_at,
  };
}

export function useSikaChat(): UseSikaChatState {
  const { session } = useAuthSession();
  const supabase = useMemo(() => getBrowserClient(), []);
  const instanceId = useId();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Prevent loadHistory from overwriting messages during active streaming
  const isStreamingRef = useRef(false);
  const [goalId, setGoalId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const idCounter = useRef(0);

  const newSession = useCallback(() => {
    setGoalId(null);
    setSessionId(null);
    setMessages([]);
    setError(null);
    setStreamingText("");
  }, []);

  const loadSession = useCallback((sid: string, gid: string | null) => {
    setSessionId(sid);
    setGoalId(gid);
    setMessages([]);
    setError(null);
    setStreamingText("");
    // History will auto-reload via the useEffect on sessionId/goalId change
  }, []);

  const loadHistory = useCallback(async () => {
    if (!session?.user) { setMessages([]); setLoadingHistory(false); return; }
    if (!goalId && !sessionId) { setMessages([]); setLoadingHistory(false); return; }
    // Don't wipe messages while actively streaming — the Realtime subscription
    // or a sessionId/goalId change can trigger this during tool execution.
    if (isStreamingRef.current) { setLoadingHistory(false); return; }
    setLoadingHistory(true);
    const query = supabase.from("sika_messages").select("*")
      .eq("user_id", session.user.id).order("created_at", { ascending: false }).limit(200);
    if (goalId) query.eq("goal_id", goalId);
    else query.eq("session_id", sessionId!);
    const { data, error: fetchErr } = await query;
    if (fetchErr) { setError(fetchErr.message); setLoadingHistory(false); return; }
    setMessages((data ?? []).filter((r) => r.role !== "tool" || r.card_type != null).reverse().map(rowToMessage));
    setLoadingHistory(false);
  }, [supabase, session, goalId, sessionId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Realtime: surface proactive messages without reload
  useEffect(() => {
    if (!session?.user) return;
    const ch = supabase
      .channel(`sika-rt:${instanceId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "sika_messages", filter: `user_id=eq.${session.user.id}` }, (p) => {
        const row = p.new as SikaMessageRow;
        if (row.role === "tool" && row.card_type == null) return;
        setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, rowToMessage(row)]));
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [supabase, session, instanceId]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !session?.access_token) return;

      setError(null);
      setSending(true);
      setStreamingText("");
      setAgentSteps([]);
      isStreamingRef.current = true;

      const optimisticId = `optimistic-${String(++idCounter.current)}`;
      const userMsg: ChatMessage = {
        id: optimisticId,
        role: "user",
        content: trimmed,
        tool_use: null,
        card_type: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        // ── Try SSE streaming first ──
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 120_000);

        const resp = await fetch("/api/sika/stream", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ message: trimmed, goal_id: goalId, session_id: sessionId }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!resp.ok || !resp.body) {
          // Fallback to non-streaming endpoint
          await sendFallback(trimmed, optimisticId);
          return;
        }

        // Parse SSE stream
        // Cards are buffered and flushed AFTER the assistant's text
        // to prevent action buttons from appearing mid-thought.
        // Agent deployment steps are tracked live during streaming.
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let accumulated = "";
        let pendingCards: ChatMessage[] = [];
        let currentEventType = "";
        // Track tool_ids for agent steps — used to update step status
        const liveSteps: AgentStep[] = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEventType = line.slice(7).trim();
              continue;
            }
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            let event: Record<string, unknown>;
            try { event = JSON.parse(raw) as Record<string, unknown>; } catch { continue; }

            // ── tool_start → live agent deployment step (queued → active)
            if (currentEventType === "tool_start" || (event.tool_name !== undefined && event.tool_id !== undefined && event.result === undefined && event.card_type === undefined)) {
              const toolName = event.tool_name as string;
              const toolId = event.tool_id as string;
              const label = TOOL_LABELS[toolName] ?? toolName;
              liveSteps.push({ id: toolId, label, status: "active" as AgentStepStatus });
              setAgentSteps([...liveSteps]);
              continue;
            }

            // ── text_delta
            if (currentEventType === "text_delta" || event.text !== undefined) {
              accumulated += (event.text as string) ?? "";
              setStreamingText(accumulated);
            }

            // ── tool_result → update step status to done + buffer card
            if (currentEventType === "tool_result" || (event.result !== undefined && event.tool_id !== undefined)) {
              const toolId = event.tool_id as string;
              // Mark the matching step as done
              const stepIdx = liveSteps.findIndex((s) => s.id === toolId);
              if (stepIdx >= 0) {
                liveSteps[stepIdx] = { ...liveSteps[stepIdx]!, status: "done" as AgentStepStatus };
                setAgentSteps([...liveSteps]);
              }

              // Buffer card if this tool result has a card_type
              if (event.card_type !== undefined && event.card_type !== null) {
                const cardMsg: ChatMessage = {
                  id: `card-${String(++idCounter.current)}`,
                  role: "tool",
                  content: JSON.stringify(event.result),
                  tool_use: {
                    id: event.tool_id,
                    name: event.tool_name,
                    result: event.result,
                  },
                  card_type: event.card_type as string,
                  created_at: new Date().toISOString(),
                };
                pendingCards.push(cardMsg);
              }
            }

            // ── message_complete → flush text + cards
            if (currentEventType === "message_complete" || (event.content !== undefined && event.session_id !== undefined)) {
              const finalContent = (event.content as string) ?? accumulated;
              const newGoalId = event.goal_id as string | null;
              const newSessionId = event.session_id as string | null;

              if (newSessionId && !sessionId) setSessionId(newSessionId);
              if (newGoalId && !goalId) setGoalId(newGoalId);

              // Only add the assistant message if there's actual text content
              const cardsToFlush = [...pendingCards];
              pendingCards = [];

              if (finalContent.trim()) {
                const assistantMsg: ChatMessage = {
                  id: `resp-${String(++idCounter.current)}`,
                  role: "assistant",
                  content: finalContent,
                  tool_use: null,
                  card_type: null,
                  created_at: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantMsg, ...cardsToFlush]);
              } else if (cardsToFlush.length > 0) {
                // No text but there are cards — just flush the cards
                setMessages((prev) => [...prev, ...cardsToFlush]);
              }

              setStreamingText("");
              accumulated = "";
              // Clear agent steps after message completes
              setAgentSteps([]);
            }

            // ── error
            // SSE error events from /api/sika/stream may carry the
            // structured Anthropic 400 fields (errType + errMessage)
            // from PR #11. Prefer those over the generic `error`
            // wrapper so the user sees the actual provider complaint
            // and we can diagnose without digging into Vercel runtime
            // logs.
            if (currentEventType === "error" || (event.error !== undefined && event.text === undefined)) {
              const errMessage = (event as { errMessage?: unknown }).errMessage;
              const errType = (event as { errType?: unknown }).errType;
              const baseError = event.error as string | undefined;
              let composed: string;
              if (typeof errMessage === "string" && errMessage.length > 0) {
                composed = typeof errType === "string" && errType.length > 0 && errType !== "unknown"
                  ? `${errType}: ${errMessage}`
                  : errMessage;
              } else if (typeof baseError === "string") {
                composed = baseError;
              } else {
                composed = "Unknown error";
              }
              setError(composed);
            }

            // Reset event type after processing data line
            currentEventType = "";
          }
        }

        // Flush any remaining cards that arrived without a final message_complete
        if (pendingCards.length > 0) {
          setMessages((prev) => [...prev, ...pendingCards]);
          pendingCards = [];
        }
        setAgentSteps([]);
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        if (err instanceof DOMException && err.name === "AbortError") {
          setError("Request timed out. SIKA may be busy — please try again.");
        } else {
          setError(err instanceof Error ? err.message : "Network error");
        }
      } finally {
        setSending(false);
        setStreamingText("");
        setAgentSteps([]);
        isStreamingRef.current = false;
      }
    },
    [session, goalId, sessionId],
  );

  // Fallback: non-streaming /api/sika/chat
  const sendFallback = useCallback(
    async (text: string, optimisticId: string) => {
      try {
        const resp = await fetch("/api/sika/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session!.access_token}`,
          },
          body: JSON.stringify({ message: text, goal_id: goalId, session_id: sessionId }),
        });

        const json = (await resp.json()) as {
          status?: string;
          messages?: Array<{ role: string; content: string; tool_use?: unknown; card_type?: string | null }>;
          goal_id?: string | null; session_id?: string | null; error?: string; error_text?: string | null;
        };

        if (!resp.ok) {
          setError(json.error ?? `Server error ${String(resp.status)}`);
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setSending(false);
          return;
        }

        if (json.session_id && !sessionId) setSessionId(json.session_id);
        if (json.goal_id && !goalId) setGoalId(json.goal_id);

        const newMessages: ChatMessage[] = (json.messages ?? [])
          .filter((m) => m.role !== "tool" || m.card_type != null)
          .map((m) => ({
            id: `resp-${String(++idCounter.current)}`,
            role: m.role as ChatMessage["role"],
            content: m.content ?? "",
            tool_use: m.tool_use ?? null,
            card_type: m.card_type ?? null,
            created_at: new Date().toISOString(),
          }));

        setMessages((prev) => [...prev, ...newMessages]);
      } catch (err) {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
        setError(err instanceof Error ? err.message : "Network error");
      } finally {
        setSending(false);
      }
    },
    [session, goalId, sessionId],
  );

  return useMemo(
    () => ({ messages, sending, streamingText, agentSteps, error, goalId, sessionId, setGoalId, send, newSession, loadSession, loadingHistory }),
    [messages, sending, streamingText, agentSteps, error, goalId, sessionId, setGoalId, send, newSession, loadSession, loadingHistory],
  );
}
